import {
  MinusIcon,
  SquarePenIcon,
  PanelRightIcon,
  PictureInPicture2Icon,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@f2e/ui/components/base"
import { TipIconButton } from "@f2e/ui/components/Button"
import { cn } from "@f2e/ui/lib/utils"

import { CopilotHistory } from "@/types"

import CopilotHistoryDropdown from "./CopilotHistoryDropdown"
import type { CopilotMode } from "./types"

interface CopilotHeaderProps {
  className?: string
  mode: CopilotMode
  histories: CopilotHistory[]
  selectedHistoryId?: string
  onModeToggle?: () => void
  onNewChat?: () => void
  onSelectHistory?: (history: CopilotHistory) => void
  onClose?: () => void
  isBusy?: boolean
}

const CopilotHeader = ({
  className,
  mode,
  histories,
  selectedHistoryId,
  onModeToggle,
  onNewChat,
  onSelectHistory,
  onClose,
  isBusy = false,
}: CopilotHeaderProps) => {
  const { t } = useTranslation()

  const isDrawerMode = mode === "drawer"

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-0 border-b border-border px-4 py-1",
        className
      )}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0 h-10">
        <CopilotHistoryDropdown
          className="rounded-full h-8 py-0"
          histories={histories}
          selectedId={selectedHistoryId}
          onSelectHistory={onSelectHistory}
          disabled={isBusy}
        />
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <TipIconButton
          tip={t("common.copilot.header.newChat")}
          button={
            <Button
              className="rounded-full border-0 h-8 w-8"
              aria-label={
                isDrawerMode
                  ? t("common.copilot.header.switchToFloatingAria")
                  : t("common.copilot.header.dockToRightAria")
              }
              size="icon"
              variant="ghost"
              onClick={onNewChat}
              disabled={isBusy}
            >
              <SquarePenIcon className="h-4 w-4" />
            </Button>
          }
        />
      </div>
      <div className="flex items-center gap-0 shrink-0">
        <TipIconButton
          tip={
            isDrawerMode
              ? t("common.copilot.header.switchToFloating")
              : t("common.copilot.header.switchToSidebar")
          }
          button={
            <Button
              className="rounded-full border-0 h-8 w-8"
              aria-label={
                isDrawerMode
                  ? t("common.copilot.header.switchToFloatingAria")
                  : t("common.copilot.header.dockToRightAria")
              }
              size="icon"
              variant="ghost"
              onClick={onModeToggle}
            >
              {isDrawerMode ? (
                <PictureInPicture2Icon className="h-4 w-4" />
              ) : (
                <PanelRightIcon className="h-4 w-4" />
              )}
            </Button>
          }
        />
        <TipIconButton
          tip={t("common.copilot.header.minimize")}
          button={
            <Button
              className="rounded-full border-0 h-8 w-8"
              aria-label={t("common.copilot.header.minimizeAria")}
              size="icon"
              variant="ghost"
              onClick={onClose}
            >
              <MinusIcon className="h-4 w-4" />
            </Button>
          }
        />
      </div>
    </div>
  )
}

export default CopilotHeader
